/**
 * Shared console chrome. The host supplies identity, service name, storage key
 * and links; this component owns only responsive navigation behaviour.
 */

import { useRef, useState, type ReactElement, type ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import {
  Body1Strong,
  Button,
  Caption1,
  DrawerBody,
  DrawerHeader,
  OverlayDrawer,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import {
  ArrowExitRegular,
  DismissRegular,
  NavigationRegular,
  PanelLeftContractRegular,
  PanelLeftExpandRegular,
} from '@fluentui/react-icons'
import { Pill } from '../platform'
import { WINDOW_BREAKPOINTS, useContainerWidth } from '../sdk/useContainerWidth'
import { useLocale } from '../i18n'
import { ConsoleLogo, ConsoleMark } from './ConsoleMark'

const EXPANDED = '240px'
const COLLAPSED = '48px'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    height: '100%',
    minWidth: 0,
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  rootMobile: {
    flexDirection: 'column',
  },
  content: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
  },
  rail: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    height: '100%',
    boxSizing: 'border-box',
    width: EXPANDED,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    gap: tokens.spacingVerticalXS,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: 'hidden',
  },
  railCollapsed: {
    width: COLLAPSED,
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minHeight: '24px',
    padding: `0 ${tokens.spacingHorizontalS}`,
    marginBottom: tokens.spacingVerticalM,
    minWidth: 0,
  },
  brandCollapsed: {
    justifyContent: 'center',
    padding: 0,
  },
  brandText: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  entries: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    padding: `0 ${tokens.spacingHorizontalXS}`,
  },
  entry: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minHeight: '40px',
    boxSizing: 'border-box',
    padding: `0 ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    color: tokens.colorNeutralForeground2,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    ':hover': { backgroundColor: tokens.colorNeutralBackground2Hover },
    ':focus-visible': {
      outlineColor: tokens.colorStrokeFocus2,
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineOffset: '-2px',
    },
  },
  entryCollapsed: {
    justifyContent: 'center',
    padding: 0,
  },
  entryMobile: {
    minHeight: '44px',
  },
  entryActive: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    fontWeight: tokens.fontWeightSemibold,
  },
  entryLabel: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  foot: {
    marginTop: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalS} 0`,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  footCollapsed: {
    alignItems: 'center',
    paddingLeft: tokens.spacingHorizontalXXS,
    paddingRight: tokens.spacingHorizontalXXS,
  },
  identity: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
  },
  email: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: tokens.colorNeutralForeground3,
  },
  full: {
    width: '100%',
    justifyContent: 'flex-start',
  },
  topBar: {
    height: '48px',
    minHeight: '48px',
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `0 ${tokens.spacingHorizontalXS} 0 ${tokens.spacingHorizontalM}`,
    boxSizing: 'border-box',
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  topBarTitle: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  menuButton: {
    width: '44px',
    minWidth: '44px',
    height: '44px',
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    minHeight: '48px',
  },
  drawerTitle: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  drawerBody: {
    display: 'flex',
    flexDirection: 'column',
    padding: `0 ${tokens.spacingHorizontalXS} ${tokens.spacingVerticalM}`,
  },
  drawerEntries: {
    padding: 0,
  },
  drawerFoot: {
    marginTop: 'auto',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalS} 0`,
  },
  touchButton: {
    minHeight: '44px',
  },
})

export interface ConsoleNavEntry {
  value: string
  label: string
  path: string
  icon: ReactElement
}

export interface ConsoleIdentity {
  email: string
  role: string
}

/** Where the host's status control sits: the open rail, the icon rail, or
 *  the phone's top bar, where it stays visible without opening the menu. */
export type StatusPlacement = 'rail' | 'rail-collapsed' | 'top-bar'

export interface ConsoleNavProps {
  entries: ConsoleNavEntry[]
  identity: ConsoleIdentity
  serviceName: string
  storageKey: string
  onSignOut: () => void
  status?: (placement: StatusPlacement) => ReactNode
  children: ReactNode
}

export function ConsoleNav({
  children,
  entries,
  identity,
  onSignOut,
  serviceName,
  status,
  storageKey,
}: ConsoleNavProps) {
  const styles = useStyles()
  const { t } = useLocale()
  const root = useRef<HTMLDivElement>(null)
  const available = useContainerWidth(root)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [pinned, setPinned] = usePinnedRail(storageKey)

  const mobile = available > 0 && available < WINDOW_BREAKPOINTS.md
  const tablet = available >= WINDOW_BREAKPOINTS.md && available < WINDOW_BREAKPOINTS.lg
  const collapsed = tablet || !pinned

  return (
    <div ref={root} className={mergeClasses(styles.root, mobile && styles.rootMobile)}>
      {mobile ? (
        <>
          <header className={styles.topBar}>
            <ConsoleMark size={24} />
            <Body1Strong className={styles.topBarTitle}>{serviceName}</Body1Strong>
            {status?.('top-bar')}
            <Button
              className={styles.menuButton}
              appearance="subtle"
              icon={<NavigationRegular />}
              aria-label={t('console.nav.menu')}
              onClick={() => setDrawerOpen(true)}
            />
          </header>
          <OverlayDrawer
            open={drawerOpen}
            position="start"
            size="small"
            onOpenChange={(_, data) => setDrawerOpen(data.open)}
          >
            <DrawerHeader className={styles.drawerHeader}>
              <div className={styles.drawerTitle}>
                <ConsoleMark size={24} />
                <Body1Strong className={styles.brandText}>{serviceName}</Body1Strong>
              </div>
              <Button
                className={styles.menuButton}
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label={t('console.nav.close')}
                onClick={() => setDrawerOpen(false)}
              />
            </DrawerHeader>
            <DrawerBody className={styles.drawerBody}>
              <nav aria-label={t('console.nav.label')}>
                <div className={mergeClasses(styles.entries, styles.drawerEntries)}>
                  {entries.map((entry) => (
                    <ConsoleLink
                      key={entry.value}
                      entry={entry}
                      mobile
                      onNavigate={() => setDrawerOpen(false)}
                    />
                  ))}
                </div>
              </nav>
              <AccountActions
                className={mergeClasses(styles.foot, styles.drawerFoot)}
                identity={identity}
                onSignOut={onSignOut}
                signOutLabel={t('console.nav.signOut')}
                touch
              />
            </DrawerBody>
          </OverlayDrawer>
        </>
      ) : (
        <nav
          className={mergeClasses(styles.rail, collapsed && styles.railCollapsed)}
          aria-label={t('console.nav.label')}
        >
          <div className={mergeClasses(styles.brand, collapsed && styles.brandCollapsed)}>
            {collapsed ? <ConsoleMark size={24} /> : <ConsoleLogo height={20} />}
            {!collapsed ? <Body1Strong className={styles.brandText}>{serviceName}</Body1Strong> : null}
          </div>

          <div className={styles.entries}>
            {entries.map((entry) => (
              <ConsoleLink key={entry.value} entry={entry} collapsed={collapsed} />
            ))}
          </div>

          <AccountActions
            className={mergeClasses(styles.foot, collapsed && styles.footCollapsed)}
            collapsed={collapsed}
            identity={identity}
            onSignOut={onSignOut}
            signOutLabel={t('console.nav.signOut')}
            status={status?.(collapsed ? 'rail-collapsed' : 'rail')}
          >
            {!tablet ? (
              <Tooltip
                content={pinned ? t('console.nav.collapse') : t('console.nav.expand')}
                relationship="label"
                withArrow
              >
                <Button
                  className={collapsed ? undefined : styles.full}
                  appearance="subtle"
                  icon={pinned ? <PanelLeftContractRegular /> : <PanelLeftExpandRegular />}
                  aria-label={pinned ? t('console.nav.collapse') : t('console.nav.expand')}
                  onClick={() => setPinned(!pinned)}
                >
                  {collapsed ? undefined : t('console.nav.collapseShort')}
                </Button>
              </Tooltip>
            ) : null}
          </AccountActions>
        </nav>
      )}
      <div className={styles.content} data-console-content>
        {children}
      </div>
    </div>
  )
}

function ConsoleLink({
  collapsed = false,
  entry,
  mobile = false,
  onNavigate,
}: {
  collapsed?: boolean
  entry: ConsoleNavEntry
  mobile?: boolean
  onNavigate?: () => void
}) {
  const styles = useStyles()
  const link = (
    <NavLink
      to={entry.path}
      end={entry.path === '/'}
      className={({ isActive }) =>
        mergeClasses(
          styles.entry,
          collapsed && styles.entryCollapsed,
          mobile && styles.entryMobile,
          isActive && styles.entryActive,
        )
      }
      aria-label={collapsed ? entry.label : undefined}
      onClick={onNavigate}
    >
      {entry.icon}
      {!collapsed ? <span className={styles.entryLabel}>{entry.label}</span> : null}
    </NavLink>
  )

  return collapsed ? (
    <Tooltip content={entry.label} relationship="label" withArrow positioning="after">
      {link}
    </Tooltip>
  ) : (
    link
  )
}

function AccountActions({
  children,
  className,
  collapsed = false,
  identity,
  onSignOut,
  signOutLabel,
  status,
  touch = false,
}: {
  children?: ReactNode
  className: string
  collapsed?: boolean
  identity: ConsoleIdentity
  onSignOut: () => void
  signOutLabel: string
  status?: ReactNode
  touch?: boolean
}) {
  const styles = useStyles()
  const signOutButton = (
    <Button
      className={mergeClasses(!collapsed && styles.full, touch && styles.touchButton)}
      appearance="subtle"
      icon={<ArrowExitRegular />}
      // Named by its own text where it has text, and by this where it is an
      // icon alone. Never by the tooltip.
      aria-label={collapsed ? signOutLabel : undefined}
      onClick={onSignOut}
    >
      {collapsed ? undefined : signOutLabel}
    </Button>
  )
  return (
    <footer className={className}>
      {status}
      {!collapsed ? (
        <div className={styles.identity}>
          <Caption1 className={styles.email}>{identity.email}</Caption1>
          <div>
            <Pill>{identity.role}</Pill>
          </div>
        </div>
      ) : null}
      {/* The email describes the button, it does not name it.
        *
        * This was a Tooltip with relationship="label", which declares the
        * tooltip to be the button's accessible name. So the control that signs
        * you out announced itself as your email address, and it competed with
        * the aria-label beside it: two labels, one of them false, on the only
        * button here with a consequence.
        *
        * And only where it adds something. Expanded, the email is already on
        * screen two lines above, so a tooltip repeating it is noise; collapsed,
        * the rail shows no identity at all and the tooltip is the one place it
        * appears. */}
      {collapsed ? (
        <Tooltip content={identity.email} relationship="description" withArrow>
          {signOutButton}
        </Tooltip>
      ) : (
        signOutButton
      )}
      {children}
    </footer>
  )
}

/** Remembered per console, so hosts do not fight over one preference. */
function usePinnedRail(storageKey: string): [boolean, (value: boolean) => void] {
  const [pinned, set] = useState(() => {
    try {
      return localStorage.getItem(storageKey) !== 'false'
    } catch {
      return true
    }
  })

  return [
    pinned,
    (value: boolean) => {
      try {
        localStorage.setItem(storageKey, String(value))
      } catch {
        // Storage can be disabled; the rail remains usable for this session.
      }
      set(value)
    },
  ]
}
